import { Chart, ChartType, TooltipItem, TooltipModel } from 'chart.js';

interface CustomTooltipContext<T extends ChartType> {
  chart: Chart;
  tooltip: TooltipModel<T>;
}

export function customTooltipCallback<T extends ChartType = ChartType>(
  context: CustomTooltipContext<T>,
  getDisplayValue?: (tooltip: TooltipItem<T>) => string
) {
  const chartId = context.chart.id;
  const tooltipId = `chart-tooltip-${chartId}`;
  let tooltipEl = document.getElementById(tooltipId);

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('span');
    tooltipEl.classList.add('chart__tooltip');
    tooltipEl.id = tooltipId;
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  const tooltipModel = context.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  // Set Text
  if (tooltipModel.dataPoints) {
    tooltipEl.innerText = (getDisplayValue ?? defaultGetDisplayValue)(
      tooltipModel.dataPoints[0]
    );
  }

  const position = context.chart.canvas.getBoundingClientRect();

  // Display, position, and set styles for font
  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.left =
    getLeftPosition(tooltipEl, position, tooltipModel) + 'px';
  tooltipEl.style.top =
    getTopPosition(tooltipEl, position, tooltipModel) + 'px';
  tooltipEl.style.pointerEvents = 'none';
}

function defaultGetDisplayValue<T extends ChartType>(tooltip: TooltipItem<T>) {
  return tooltip.formattedValue;
}

function getLeftPosition<T extends ChartType>(
  tooltip: HTMLElement,
  position: DOMRect,
  tooltipModel: TooltipModel<T>
) {
  let pos = position.left + window.scrollX + tooltipModel.caretX;
  if (pos + tooltip.clientWidth > window.innerWidth + window.scrollX) {
    pos -= tooltip.clientWidth;
  }

  return pos;
}

function getTopPosition<T extends ChartType>(
  tooltip: HTMLElement,
  position: DOMRect,
  tooltipModel: TooltipModel<T>
) {
  let pos = position.top + window.scrollY + tooltipModel.caretY;
  if (pos + tooltip.clientHeight > window.innerHeight + window.scrollY) {
    pos -= tooltip.clientHeight;
  }

  return pos;
}
